import { Component, Input, OnInit } from '@angular/core';
import { addDays } from '@fullcalendar/core/internal.js';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, of, switchMap, tap } from 'rxjs';
import { InputMode } from '../../../core/editable-fields/text-input/text-input.component';
import { UtilityService, toStandardDate } from '../../../core/services/utility.service';
import { SolarWindsService } from '../../../services/solar-winds.service';


@Component({
    selector: 'my-courses-recording-request-modal',
    templateUrl: './my-courses-recording-request.component.html',
    styleUrls: ['./my-courses-recording-request.component.scss'],
    standalone: false
})
export class MyCoursesRecordingRequestModalComponent implements OnInit {
    @Input() section: any;
    @Input() studentId: number;

    public recordingMinDate: Date = new Date();
    public now: Date = new Date();
    public isSubmitting: boolean = false;
    public allLabelCols: number = 4;
    public classrooms: any[] = [];

    public request: any = {
        errors$: new BehaviorSubject({}),
        warnings$: new BehaviorSubject({}),
        sectionId: '',
        date: '',
        location: '',
        message: '',
        personId: ''
    }

    public classrooms$: Observable<any>;
    public submitTicket$: Subject<any> = new Subject<any>();

        public get inputMode(): typeof InputMode {
        return InputMode;
    }

    constructor(
        public appService: SolarWindsService,
        public activeModal: NgbActiveModal,
        public utilityService: UtilityService,
        public toastr: ToastrService,
    ) {
        this.request.onChange = () => { this.request.IsDirty = true };
        this.request.validate = () => this.validate(this.request);
    }

    public ngOnInit(): void {
        const classStartDate = new Date(this.section.StartDate)
        if (classStartDate > new Date())
            this.recordingMinDate = classStartDate;

        //  We need this to be the start of the day for validation;
        this.recordingMinDate = new Date(this.recordingMinDate.getFullYear(), this.recordingMinDate.getMonth(), this.recordingMinDate.getDate());

        this.classrooms$ = this.utilityService.lookups$(['SWClassroomSite']).pipe(
            tap((lookups) => {
                this.classrooms = lookups["SWClassroomSite"];
                const firstMeeting = this.section.Meetings[0];

                if (firstMeeting != null) {
                    const room = `${firstMeeting["Building"].trim()} ${firstMeeting["Room"].trim()}`;

                    if (!!this.classrooms[room])
                        this.request.location = { name: room, id: this.classrooms[room] };
                }
            }),
        );
    }

    public validate(obj: any) {
        obj.errors = {};
        obj.warnings = {};
        obj.hasErrors = false;
        this.request.validate = () => this.validate(this.request);
        var requiredText = 'Required Field';

        if (!obj.date || obj.date == UtilityService.nullDateString)
            obj.errors['date'] = requiredText;
        else {
            const currentDate = new Date(obj.date);
            const endDate = new Date(this.section.EndDate);

            if ((currentDate < this.recordingMinDate) || (currentDate > endDate))
                obj.errors['date'] = `Must select a date between ${toStandardDate(this.recordingMinDate)} and ${toStandardDate(endDate)}`;
        }

        if (!obj.location)
            obj.errors['location'] = requiredText;

        obj.syncAlerts?.();

        if (Object.keys(obj.errors).length)
            obj.hasErrors = true;
    }

    public addDaysToDate(date: Date, days: number) {
        return addDays(date, days);
    }

    public classroomList$(query: string): Observable<any[]> {
        return of(
            Object.keys(this.classrooms)
                .filter(key => key.toLowerCase().includes(query?.toLowerCase()))
                .map(key => ({ name: key, id: this.classrooms[key] }))
        );
    }

    public submit() {
        this.isSubmitting = true;
        let formData = new FormData();

        let toSubmit: any = {
            type: 'class',
            sectionId: this.section.Id,
            startDate: this.request.date,
            message: this.request.message,
            location: this.request.location?.name,
            locationId: this.request.location?.id,
        }

        formData.append('request', JSON.stringify(toSubmit));

        this.submitTicket$.next(formData);
    }

    public submit$ = this.submitTicket$.pipe(
        switchMap((data) => {
            this.isSubmitting = true;
            return this.appService.createRecording$(data);
        }),
        tap((result) => {
            if (result?.IsSucceeded) {
                this.toastr.success("Class recording request successfully submitted.");
                this.activeModal.dismiss('close');
            }
            else {
                let message = "There was an error submitting your request.";

                if (result.error?.Message)
                    message = result.error.Message;

                if (result?.error)
                    message = result.error;

                this.toastr.error(message);
            }

            this.isSubmitting = false;
        }
        ),
    )
}
